import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "searchResults", "dropdown"]
  static values = { searchUrl: String }

  connect() {
    this.searchTimeout = null
    this.isOpen = false
  }

  disconnect() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.closeDropdown()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.isOpen) {
      this.closeDropdown()
    } else {
      this.openDropdown()
    }
  }

  openDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    this.isOpen = true
    this.searchInputTarget.focus()
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside.bind(this))
  }

  closeDropdown() {
    this.dropdownTarget.classList.add('hidden')
    this.isOpen = false
    this.clearSearch()
    
    // Remove event listener
    document.removeEventListener('click', this.handleClickOutside.bind(this))
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  search(event) {
    const query = event.target.value.trim()
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    
    if (query.length < 2) {
      this.clearResults()
      return
    }

    // Debounce search
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  async performSearch(query) {
    try {
      const response = await fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const users = await response.json()
        this.displayResults(users)
      } else {
        this.showError('Search failed')
      }
    } catch (error) {
      console.error('User search error:', error)
      this.showError('Search error occurred')
    }
  }

  displayResults(users) {
    if (users.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="px-4 py-2 text-sm text-gray-500 dark:text-night-text-muted">
          No users found
        </div>
      `
      return
    }

    const resultsHtml = users.map(user => `
      <form action="/switch_user/${user.id}" method="post" class="block">
        <input type="hidden" name="authenticity_token" value="${this.getCSRFToken()}">
        <button type="submit" class="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-night-surface-hover text-sm transition-colors">
          <div class="flex items-center justify-between">
            <div>
              <div class="font-medium text-gray-900 dark:text-night-text-primary">${user.display_name}</div>
              <div class="text-xs text-gray-500 dark:text-night-text-muted">@${user.username}</div>
            </div>
            ${user.admin ? '<span class="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">Admin</span>' : ''}
          </div>
        </button>
      </form>
    `).join('')

    this.searchResultsTarget.innerHTML = resultsHtml
  }

  showError(message) {
    this.searchResultsTarget.innerHTML = `
      <div class="px-4 py-2 text-sm text-red-600">
        ${message}
      </div>
    `
  }

  clearResults() {
    this.searchResultsTarget.innerHTML = ''
  }

  clearSearch() {
    this.searchInputTarget.value = ''
    this.clearResults()
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }
}